import { markRaw, reactive } from 'vue'

let idCounter = 0

/**
 * Pila global de modales.
 * Cada entrada representa una capa modal abierta.
 * El orden del array determina el orden de apilamiento (el último está arriba).
 */
export const modalStack = reactive([])

const generateId = () => `modal-${++idCounter}`

const createBaseEntry = (overrides = {}) => ({
    id: generateId(),
    type: null,
    title: '',
    message: '',
    buttonText: 'Aceptar',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    icon: 'info',
    closeOnOverlay: true,
    closeOnEscape: true,
    component: null,
    props: null,
    resolve: null,
    reject: null,
    onConfirm: null,
    onCancel: null,
    onClose: null,
    onBeforeClose: null,
    closeResult: null,
    preloadingClose: null,
    ...overrides
})

const pushModal = (entry) => {
    modalStack.push(entry)
    return entry.id
}

const closeById = (id, result) => {
    const idx = modalStack.findIndex((m) => m.id === id)
    if (idx === -1) return

    const modal = modalStack[idx]

    if (modal.onBeforeClose) {
        const canClose = modal.onBeforeClose()
        if (canClose === false) return
    }

    const finalResult = result !== undefined ? result : modal.closeResult
    if (modal.resolve) modal.resolve(finalResult)

    modalStack.splice(idx, 1)
}

export const setModalCallback = (id, key, value) => {
    const modal = modalStack.find((m) => m.id === id)
    if (modal) modal[key] = value
}

export const useCustomModal = () => {
    const alert = (config = {}) => {
        const id = pushModal(createBaseEntry({
            type: 'alert',
            title: config.title || 'Información',
            message: config.message || '',
            buttonText: config.buttonText || 'Aceptar',
            icon: config.icon,
            closeOnOverlay: config.closeOnOverlay !== false,
            closeOnEscape: config.closeOnEscape !== false
        }))

        const modal = modalStack.find((m) => m.id === id)

        modal.onConfirm = () => closeById(id)
        modal.onCancel = () => closeById(id)

        return new Promise((resolve) => {
            modal.resolve = resolve
        })
    }

    const confirm = (config = {}) => {
        const id = pushModal(createBaseEntry({
            type: 'confirm',
            title: config.title || '¿Confirmar acción?',
            message: config.message || '¿Estás seguro de que deseas continuar?',
            confirmText: config.confirmText || 'Confirmar',
            cancelText: config.cancelText || 'Cancelar',
            icon: config.icon,
            closeOnOverlay: config.closeOnOverlay !== false,
            closeOnEscape: config.closeOnEscape !== false
        }))

        const modal = modalStack.find((m) => m.id === id)

        modal.onConfirm = () => closeById(id, true)
        modal.onCancel = () => closeById(id, false)

        return new Promise((resolve) => {
            modal.resolve = resolve
        })
    }

    const custom = (component, props = {}, options = {}) => {
        // savePrevious ya no tiene efecto: todos los modales se apilan por defecto.
        const id = pushModal(createBaseEntry({
            type: 'custom',
            closeOnOverlay: options.closeOnOverlay || false,
            closeOnEscape: options.closeOnEscape !== false,
            hideCloseButton: options.hideCloseButton ?? false,
            component: markRaw(component),
            props
        }))

        const modal = modalStack.find((m) => m.id === id)

        return new Promise((resolve) => {
            modal.resolve = resolve
        })
    }

    const preload = (config = {}) => {
        const id = pushModal(createBaseEntry({
            type: 'preload',
            message: config.message || 'Cargando',
            icon: config.icon || 'eos-icons:three-dots-loading',
            closeOnOverlay: false,
            closeOnEscape: false
        }))

        return {
            close: () => closeById(id)
        }
    }

    const close = (id, result) => closeById(id, result)

    const closeTop = (result) => {
        const top = modalStack[modalStack.length - 1]
        if (top) closeById(top.id, result)
    }

    const closeAll = () => {
        modalStack.splice(0, modalStack.length)
    }

    return {
        alert,
        confirm,
        custom,
        preload,
        close,
        closeTop,
        closeAll
    }
}
